import { SmsOutlined } from "@material-ui/icons"
import { useStorage } from "hooks"
import {
    CREDENTIALS_ERROR,
    ERROR_PATH,
    FORBIDDEN_PATH,
    getUserInfoByTokenService,
    isForbidden,
    isUnauthenticatedPath,
    LOCAL_STORAGE_ACCOUNT_KEY,
    LOCAL_STORAGE_SECURITY_KEY,
    loginService,
    NO_MODULES_ASIGNED_ERROR,
    ROOT_PATH
} from "lib"
import React from "react"
import { useHistory, useLocation } from "react-router"
import { IInfoUserResponse, IModule, IUser } from "types"
import { IUserApproval } from "types/userApproval"

export interface IAuth {
    user: IUser | undefined
    login: (credentials: any) => Promise<IUser | undefined>
    logout: () => void
    searchingUser: boolean
    navigation: IModule[]
    selectedNav: IModule | undefined
    setSelectedNav: React.Dispatch<IModule | undefined>
    userApproval: IUserApproval | undefined
}

interface IAuthError {
    authError: boolean
}

const useAuth = (): IAuth & IAuthError => {
    const [user, setUser] = React.useState<IUser | undefined>(undefined)
    const [navigation, setNavigation] = React.useState<IModule[]>([])
    const [selectedNav, setSelectedNav] = React.useState<IModule | undefined>(
        undefined
    )
    const [searchingUser, setSearchingUser] = React.useState<boolean>(true)
    const [searchingPath, setSearchingPath] = React.useState<boolean>(false)
    const userApproval = undefined
    const [authError, setAuthError] = React.useState(false)
    const { saveItem, deleteItem, getItem } = useStorage()
    const history = useHistory()
    const location = useLocation()

    const login = async (credentials: any): Promise<IUser | undefined> => {
        try {
            setAuthError(false)
            const tokenResponse = await loginService(credentials)
            if (tokenResponse) {
                saveItem(LOCAL_STORAGE_SECURITY_KEY, tokenResponse.token)
                const userInfo = await getUserInfoByTokenService(tokenResponse.token)
                if (userInfo) {
                    saveItem(LOCAL_STORAGE_ACCOUNT_KEY, userInfo)
                    setUser(userInfo.user)
                    setNavigation(userInfo.modules)
                    setSelectedNav(userInfo.modules[0])
                    history.push(userInfo.modules[0].relativePath)
                    return userInfo.user
                } else {
                    throw new Error("No se pudo consultar informacion del usuario")
                }
            } else {
                throw new Error("No pudo iniciar sesion")
            }
        } catch (error: any) {
            console.error(error)
            throw new Error(
                error.message === NO_MODULES_ASIGNED_ERROR
                    ? NO_MODULES_ASIGNED_ERROR
                    : CREDENTIALS_ERROR
            )
        }
    }

    const logout = React.useCallback(() => {
        deleteItem(LOCAL_STORAGE_ACCOUNT_KEY)
        deleteItem(LOCAL_STORAGE_SECURITY_KEY)
        setUser(undefined)
        setNavigation([])
        history.replace(ROOT_PATH)
    }, [history, deleteItem])

    const searchStoredUser = React.useCallback(async () => {
        try {
            if (user) return
            setSearchingUser(true)
            const requestedPath = location.pathname
            const storedAccount: IInfoUserResponse = getItem(
                LOCAL_STORAGE_ACCOUNT_KEY
            )
            const localToken = getItem(LOCAL_STORAGE_SECURITY_KEY)
            if (localToken) {
                try {
                    setSearchingPath(true)
                    if (requestedPath !== ROOT_PATH) {
                        history.push(ROOT_PATH)
                    }
                    const userInfo = await getUserInfoByTokenService(localToken)
                    if (userInfo) {
                        setUser(userInfo.user)
                        setNavigation(userInfo.modules)
                        saveItem(LOCAL_STORAGE_ACCOUNT_KEY, userInfo)
                        setTimeout(() => {
                            if (!isForbidden(userInfo.modules, requestedPath)) {
                                const module = userInfo.modules.find((m) =>
                                    requestedPath.includes(m.relativePath)
                                )
                                if (module) {
                                    setSelectedNav(module)
                                    history.replace(requestedPath)
                                    setSearchingPath(false)
                                } else if (requestedPath === ROOT_PATH && !searchingPath) {
                                    setSelectedNav(userInfo.modules[0])
                                    history.push(userInfo.modules[0].relativePath)
                                }
                            } else {
                                if (
                                    isUnauthenticatedPath(requestedPath) &&
                                    storedAccount.modules.length > 0
                                ) {
                                    history.push(storedAccount.modules[0].relativePath)
                                } else {
                                    history.push({
                                        pathname: ERROR_PATH + FORBIDDEN_PATH,
                                        state: { requestedPath },
                                    })
                                }
                            }
                            setTimeout(() => {
                                setSearchingUser(false)
                            }, 500)
                        }, 600)
                    }
                } catch (error: any) {
                    setAuthError(true)
                    setSearchingUser(false)
                }
            } else {
                setTimeout(() => {
                    setSearchingUser(false)
                }, 1200)
            }
        } catch (error: any) { }
    }, [getItem, location.pathname, history, user, saveItem, searchingPath])


    React.useEffect(() => {
        if (location.pathname !== '/manualrequest' && location.pathname !== '/upload/xmls') {
            searchStoredUser()
        }else{
        }
    }, [searchStoredUser])

    return {
        user,
        login,
        logout,
        navigation,
        searchingUser,
        selectedNav,
        setSelectedNav,
        userApproval,
        authError,
    }
}

export default useAuth
